import { Zap, Info, HeartPulse, Hospital, Stethoscope, Pill, Dumbbell, Activity, Sparkles, Apple, Eye, X, ExternalLink, Star } from 'lucide-react';
import { aoiScoreTone, computeAoiContributions, AOI_FINAL_FORMULA } from '../../constants/aoiCategories';

/** Health / wellness POI fields shown on AOI Location after fetch. */
export const HEALTH_PLACE_FIELDS = [
    { key: 'hospitals', label: 'Hospitals & nursing homes', icon: Hospital, osmKeys: ['hospitals'], googleKeys: ['hospitals'] },
    { key: 'clinics', label: 'Clinics & doctors', icon: Stethoscope, osmKeys: ['clinics'], googleKeys: ['clinics'] },
    { key: 'pharmacies', label: 'Medical shops / pharmacies', icon: Pill, osmKeys: ['pharmacies'], googleKeys: ['pharmacies'] },
    { key: 'diagnostics', label: 'Diagnostics & labs', icon: Activity, osmKeys: [], googleKeys: ['diagnostics'] },
    { key: 'gyms', label: 'Gyms & fitness', icon: Dumbbell, osmKeys: ['gyms'], googleKeys: ['gyms'] },
    { key: 'spas', label: 'Spas / wellness / physio', icon: Sparkles, osmKeys: ['spas'], googleKeys: ['premiumSpa', 'recovery'] },
    { key: 'aesthetics', label: 'Skin, hair & aesthetics', icon: Sparkles, osmKeys: [], googleKeys: ['antiAging'] },
    { key: 'altMedicine', label: 'Ayurveda & alternative', icon: HeartPulse, osmKeys: [], googleKeys: [] },
    { key: 'nutrition', label: 'Nutrition & weight care', icon: Apple, osmKeys: [], googleKeys: [] },
    { key: 'eyeDental', label: 'Eye & dental care', icon: Eye, osmKeys: [], googleKeys: [] },
];

function sumKeys(counts, keys) {
    if (!counts || !keys?.length) return null;
    let total = 0;
    let found = false;
    for (const k of keys) {
        if (counts[k] != null && Number.isFinite(Number(counts[k]))) {
            total += Number(counts[k]);
            found = true;
        }
    }
    return found ? total : null;
}

/**
 * Prefer verify place lists when present; else Google / OSM counts.
 *
 * `unavailable` lists categories whose lookups all failed. Those must stay
 * null (rendered as "—"), never 0 — a failed request is not evidence that
 * there is nothing there.
 */
export function resolveHealthPlaceCounts(fetched, healthPlaces, unavailable) {
    const osm = fetched?.providers?.osm?.counts || null;
    const google = fetched?.providers?.google?.counts || null;
    const missing = new Set(unavailable || fetched?.healthUnavailable || []);
    const out = {};
    for (const field of HEALTH_PLACE_FIELDS) {
        const g = sumKeys(google, field.googleKeys);
        const o = sumKeys(osm, field.osmKeys);

        if (healthPlaces?.[field.key] && Array.isArray(healthPlaces[field.key])
            && !missing.has(field.key)) {
            /* An empty verified list is still a real answer, but if another
               provider counted something we trust the higher number. */
            const listLen = healthPlaces[field.key].length;
            out[field.key] = Math.max(listLen, g ?? 0, o ?? 0);
            continue;
        }

        if (missing.has(field.key)) {
            /* Fall back to any provider count we do have; otherwise unknown. */
            if (g != null || o != null) out[field.key] = Math.max(g ?? 0, o ?? 0);
            else out[field.key] = null;
            continue;
        }

        if (g != null && o != null) out[field.key] = Math.max(g, o);
        else if (g != null) out[field.key] = g;
        else if (o != null) out[field.key] = o;
        else out[field.key] = null;
    }
    return out;
}

export function AoiHealthVerifyModal({ categoryKey, places, radiusLabel, onClose }) {
    const field = HEALTH_PLACE_FIELDS.find(f => f.key === categoryKey);
    const label = field?.label || categoryKey;
    const list = places || [];

    return (
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/50"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="bg-white rounded-2xl w-full max-w-xl max-h-[80vh] flex flex-col shadow-soft"
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="health-verify-title"
            >
                <div className="px-5 py-4 border-b border-ink-100 flex items-start justify-between gap-3">
                    <div>
                        <div id="health-verify-title" className="font-bold text-sm text-ink-900">
                            {label} — Verify Data
                        </div>
                        <div className="text-xs text-ink-400 mt-1">
                            {list.length} place{list.length === 1 ? '' : 's'} found
                            {radiusLabel ? ` within ${radiusLabel}` : ''}
                        </div>
                    </div>
                    <button type="button" onClick={onClose} className="text-ink-400 hover:text-ink-700 p-1">
                        <X className="h-4.5 w-4" />
                    </button>
                </div>
                <div className="overflow-y-auto flex-1 px-5 py-3">
                    {list.length === 0 ? (
                        <div className="text-center py-10 text-sm text-ink-400">
                            No places found in this category{radiusLabel ? ` within ${radiusLabel}` : ''}.
                        </div>
                    ) : (
                        list.map((p, i) => (
                            <a
                                key={p.placeId || `${p.name}-${i}`}
                                href={p.mapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-start gap-3 px-3 py-2.5 rounded-xl border border-ink-100 mb-1.5 no-underline text-inherit hover:bg-ink-50 transition"
                            >
                                <div className="h-7 w-7 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center shrink-0 text-xs font-bold tabular-nums">
                                    {i + 1}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold text-sm text-ink-900">{p.name}</div>
                                    {p.address && (
                                        <div className="text-[11px] text-ink-500 mt-0.5">{p.address}</div>
                                    )}
                                    <div className="flex flex-wrap items-center gap-2 mt-1">
                                        {p.rating != null && (
                                            <span className="inline-flex items-center gap-1 text-[11px] text-amber-500 font-semibold">
                                                <Star className="h-2.5 w-2.5 fill-amber-400" /> {p.rating}
                                            </span>
                                        )}
                                        {p.open != null && (
                                            <span className={`text-[11px] font-semibold ${p.open ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                {p.open ? 'Open now' : 'Closed'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <ExternalLink className="h-3.5 w-3.5 text-ink-300 shrink-0 mt-1" />
                            </a>
                        ))
                    )}
                </div>
                <div className="px-5 py-3 border-t border-ink-100 text-[11px] text-ink-400">
                    Click a place to open it in Google Maps.
                </div>
            </div>
        </div>
    );
}

export function AoiHealthPlacesSection({
    fetched,
    selectedPlace,
    fetching,
    searchRadiusLabel,
    healthPlaces,
    onVerify,
    compact = false,
    /** When viewing a saved scorecard — counts only, no live Verify lists. */
    savedSummary = null,
}) {
    const unavailable = new Set(
        savedSummary?.unavailable || fetched?.healthUnavailable || [],
    );
    const counts = savedSummary?.counts
        ? HEALTH_PLACE_FIELDS.reduce((acc, field) => {
            const raw = savedSummary.counts?.[field.key];
            acc[field.key] = raw == null || raw === '' ? null : Number(raw);
            if (acc[field.key] != null && !Number.isFinite(acc[field.key])) acc[field.key] = null;
            return acc;
        }, {})
        : resolveHealthPlaceCounts(fetched, healthPlaces, [...unavailable]);
    const hasData = Object.values(counts).some(v => v != null);
    const placeName = savedSummary?.locationName || selectedPlace?.name;
    const radiusHint = savedSummary?.radiusLabel
        || searchRadiusLabel
        || (fetched?.searchRadiusKm != null
            ? (fetched.searchRadiusKm < 1
                ? `${Math.round(fetched.searchRadiusKm * 1000)} m`
                : `${Number(fetched.searchRadiusKm)} km`)
            : null);

    return (
        <div className="ag-card overflow-hidden h-full shadow-soft">
            <div className="px-4 py-3 border-b border-ink-100 bg-rose-50/80 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <HeartPulse className="h-4 w-4 text-rose-700 shrink-0" />
                    <div>
                        <div className="font-bold text-sm text-rose-900">Health & wellness nearby</div>
                        <p className="text-[11px] text-ink-500 mt-0.5">
                            Hospitals, clinics, medical shops, gyms and related places
                            {radiusHint ? ` within ${radiusHint}` : ''}
                            {placeName ? ` · ${placeName}` : ''}
                        </p>
                    </div>
                </div>
                {fetching && (
                    <span className="text-[11px] font-semibold text-ink-500">Fetching…</span>
                )}
            </div>
            <div className={`p-3 sm:p-4 grid gap-2 ${compact ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'}`}>
                {HEALTH_PLACE_FIELDS.map(field => {
                    const Icon = field.icon;
                    const value = counts[field.key];
                    const isUnavailable = unavailable.has(field.key);
                    const ready = value != null && !isUnavailable;
                    const placeList = healthPlaces?.[field.key];
                    const canVerify = !savedSummary && healthPlaces != null && !isUnavailable;

                    return (
                        <div
                            key={field.key}
                            className="rounded-xl border border-ink-100 bg-white px-3 py-2.5 flex items-center gap-3"
                        >
                            <div className="h-9 w-9 rounded-lg bg-rose-50 text-rose-700 flex items-center justify-center shrink-0">
                                <Icon className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wide truncate">
                                        {field.label}
                                    </div>
                                    {canVerify && (
                                        <button
                                            type="button"
                                            onClick={() => onVerify?.(field.key)}
                                            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold text-sky-700 bg-sky-50 hover:bg-sky-100 shrink-0"
                                        >
                                            <Eye className="h-3 w-3" />
                                            Verify{Array.isArray(placeList) ? ` (${placeList.length})` : ''}
                                        </button>
                                    )}
                                </div>
                                {ready ? (
                                    <div className="text-lg font-extrabold tabular-nums text-ink-900">
                                        {Number(value).toLocaleString('en-IN')}
                                    </div>
                                ) : isUnavailable ? (
                                    <div className="flex items-baseline gap-1.5">
                                        <span className="text-lg font-extrabold text-ink-300">—</span>
                                        <span className="text-[10px] font-semibold text-amber-700">
                                            lookup failed
                                        </span>
                                    </div>
                                ) : (
                                    <div className="text-sm text-ink-400 italic">
                                        {savedSummary
                                            ? 'Not recorded'
                                            : !selectedPlace
                                                ? 'Select a location'
                                                : fetching
                                                    ? 'Loading…'
                                                    : 'Fetch within radius to load'}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {!hasData && !savedSummary && selectedPlace && !fetching && (
                <p className="px-4 pb-3 text-[11px] text-ink-500">
                    Run <b>Fetch within …</b> to load health place counts for this pin. Then use <b>Verify</b> to inspect each place on Google Maps.
                </p>
            )}
            {!hasData && savedSummary && (
                <p className="px-4 pb-3 text-[11px] text-ink-500">
                    No health &amp; wellness counts were saved with this scorecard.
                </p>
            )}
        </div>
    );
}

export function AoiCategoryCard({ cat, values, suggestions, confidence, categoryScore, onChange, onApplySuggestion }) {
    return (
        <div className={`ag-card overflow-hidden border ${cat.accent.replace(/text-\S+/, '').replace(/bg-\S+/, '')}`}>
            <div className={`px-4 py-2 border-b flex items-center justify-between ${cat.accent}`}>
                <div>
                    <div className="font-bold text-sm">{cat.title}</div>
                    <div className="text-[10px] opacity-80">
                        Parent weight {cat.parentWeight}% of AOI
                        {confidence && (
                            <span className="ml-2 opacity-70">· {confidence} confidence</span>
                        )}
                    </div>
                </div>
                <div className={`px-2 py-1 rounded text-xs font-bold ${aoiScoreTone(categoryScore)}`}>
                    {categoryScore.toFixed(1)} / 100
                </div>
            </div>
            <div className="p-4 space-y-3">
                {cat.subs.map(s => {
                    const current = values[s.key] ?? 0;
                    const suggested = suggestions?.[s.key];
                    const showChip = Number.isFinite(suggested) && suggested > 0;
                    const canApply = showChip && current === 0;

                    return (
                        <div key={s.key} className="space-y-1">
                            <div className="grid grid-cols-[1fr_60px_auto] items-center gap-2">
                                <div>
                                    <div className="text-xs font-medium">{s.label}</div>
                                    <div className="text-[10px] text-ink-500">{s.weight}% internal weight</div>
                                </div>
                                <input
                                    type="number" min="0" max="100" step="1"
                                    className="ag-input text-xs py-1 text-right tabular-nums w-16"
                                    value={current}
                                    onChange={e => onChange(s.key, e.target.value)}
                                />
                                <input
                                    type="range" min="0" max="100" step="1"
                                    className="w-28"
                                    value={current}
                                    onChange={e => onChange(s.key, e.target.value)}
                                />
                            </div>
                            {showChip && (
                                <button
                                    type="button"
                                    disabled={!canApply}
                                    onClick={() => onApplySuggestion(s.key, suggested)}
                                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold transition ${canApply
                                        ? 'bg-brand-teal-50 text-brand-teal-800 ring-1 ring-brand-teal/30 hover:bg-brand-teal-100 cursor-pointer'
                                        : 'bg-ink-50 text-ink-400 ring-1 ring-ink-100 cursor-default'
                                        }`}
                                    title={canApply ? 'Click to apply suggested value' : 'Suggestion (field already has a value)'}
                                >
                                    <Zap className="h-3 w-3" />
                                    Suggested {Math.round(suggested)}
                                    {!canApply && ' · locked'}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export function AoiScoreHeadline({ preview, subtitle }) {
    const s = preview.aoiScore || 0;
    return (
        <div className="ag-card shadow-soft p-4 h-full flex flex-col justify-center bg-gradient-to-br from-brand-teal-50 via-white to-white border border-brand-teal/25">
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide text-ink-500 font-bold">Live AOI score</div>
                <div className="flex items-baseline gap-2 mt-1">
                    <div className="text-4xl font-extrabold tabular-nums text-brand-teal-800 leading-none">
                        {s.toFixed(1)}
                    </div>
                    <div className="text-sm text-ink-400 font-semibold">/ 100</div>
                </div>
                {subtitle && (
                    <div className="text-[11px] text-ink-500 mt-2 truncate" title={subtitle}>{subtitle}</div>
                )}
            </div>
        </div>
    );
}

function ProviderChip({ ok, label, detail }) {
    return (
        <div className={`rounded-lg border px-3 py-2 ${ok ? 'border-emerald-200 bg-white' : 'border-ink-100 bg-ink-50/50'}`}>
            <div className={`font-semibold ${ok ? 'text-emerald-800' : 'text-ink-600'}`}>{label}</div>
            <div className="text-ink-500 mt-0.5" title={detail}>{detail}</div>
        </div>
    );
}

export function AoiFormulaBreakdown({ categoryScores, aoiScore, compact = false }) {
    const { rows, total } = computeAoiContributions(categoryScores);
    const displayTotal = Number.isFinite(Number(aoiScore)) ? Number(aoiScore) : total;

    if (compact) {
        return (
            <div className="ag-card overflow-hidden shadow-soft">
                <div className="px-4 py-3 border-b border-ink-100">
                    <div className="font-bold text-sm text-ink-900">Final AOI calculation</div>
                    <p className="text-[10px] text-ink-500 mt-1 leading-relaxed">
                        Weighted sum · AD 25% · WM 20% · UC 15% · CE 15% · HA 10% · LD 10% · CW 5%
                    </p>
                </div>
                <div className="divide-y divide-ink-100">
                    {rows.map(r => {
                        const pct = Math.min(100, Math.max(0, r.score));
                        return (
                            <div key={r.key} className="px-4 py-2.5 flex items-center gap-3 hover:bg-ink-50/60 transition-colors">
                                <div className="w-8 shrink-0">
                                    <span className="text-[11px] font-extrabold text-ink-800">{r.short}</span>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                        <span className="text-[11px] text-ink-600 truncate">{r.title}</span>
                                        <span className="text-[10px] text-ink-400 tabular-nums shrink-0">{r.weight}%</span>
                                    </div>
                                    <div className="h-1.5 rounded-full bg-ink-100 overflow-hidden">
                                        <div
                                            className="h-full rounded-full bg-brand-teal transition-[width] duration-300"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </div>
                                <div className="w-[3.25rem] text-right shrink-0">
                                    <div className="text-xs font-bold font-mono tabular-nums text-ink-900">{r.score.toFixed(1)}</div>
                                    <div className="text-[10px] text-ink-400 font-mono tabular-nums">+{r.contribution.toFixed(2)}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="px-4 py-3 bg-brand-teal-700 text-white flex items-center justify-between gap-3">
                    <div>
                        <div className="text-sm font-bold">AOI Score</div>
                        <div className="text-[10px] text-white/70 mt-0.5">Sum of weighted contributions</div>
                    </div>
                    <span className="text-2xl font-extrabold font-mono tabular-nums">{displayTotal.toFixed(1)}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="ag-card overflow-hidden">
            <div className="px-4 py-3 border-b border-ink-100 bg-ink-50">
                <div className="font-bold text-sm text-ink-900">Final AOI calculation</div>
                <p className="text-[11px] text-ink-500 mt-1">{AOI_FINAL_FORMULA}</p>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[420px]">
                    <thead>
                        <tr className="bg-ink-50 text-ink-500 uppercase text-[10px] tracking-wide">
                            <th className="text-left px-4 py-2">Category</th>
                            <th className="text-right px-4 py-2">Score</th>
                            <th className="text-right px-4 py-2">Weight</th>
                            <th className="text-right px-4 py-2">Contribution</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100">
                        {rows.map(r => (
                            <tr key={r.key}>
                                <td className="px-4 py-2">
                                    <span className="font-bold text-ink-800">{r.short}</span>
                                    <span className="text-ink-500"> · {r.title}</span>
                                </td>
                                <td className="px-4 py-2 text-right font-mono tabular-nums">{r.score.toFixed(1)}</td>
                                <td className="px-4 py-2 text-right text-ink-500">{r.weight}%</td>
                                <td className="px-4 py-2 text-right font-mono font-bold tabular-nums">
                                    {r.contribution.toFixed(2)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="bg-brand-teal-700 text-white">
                            <td className="px-4 py-2.5 font-bold" colSpan={3}>AOI Score</td>
                            <td className="px-4 py-2.5 text-right font-extrabold font-mono tabular-nums text-lg">
                                {displayTotal.toFixed(1)}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
}

export function AoiFetchSummary({ fetched, title }) {
    const osm = fetched.providers?.osm;
    const wiki = fetched.providers?.wiki;
    const google = fetched.providers?.google;
    const isPin = fetched.scope === 'pin' || fetched.searchRadiusKm != null;
    const radiusLabel = fetched.searchRadiusKm != null
        ? (fetched.searchRadiusKm < 1
            ? `${Math.round(fetched.searchRadiusKm * 1000)} m`
            : `${fetched.searchRadiusKm} km`)
        : null;

    const tierLabel = fetched.cityTier === 'tier1'
        ? 'Tier-1 metro'
        : fetched.cityTier === 'tier2'
            ? 'Tier-2 city'
            : fetched.cityTier === 'tier3'
                ? 'Tier-3 / emerging'
                : isPin ? 'Pin catchment' : 'Local area';

    const wikiDetail = wiki?.stats?.population
        ? (wiki.districtBaseline
            ? `Est. ${(wiki.stats.population / 100000).toFixed(2)}L pop within ${radiusLabel || 'radius'}`
                + ` (district baseline ${(wiki.districtBaseline.population / 100000).toFixed(1)}L · ${Math.round(wiki.districtBaseline.area).toLocaleString('en-IN')} km²)`
            : `${(wiki.stats.population / 100000).toFixed(1)}L population${wiki.stats.area ? ` · ${Math.round(wiki.stats.area).toLocaleString('en-IN')} km²` : ''}`)
        : wiki?.error || 'No match';

    const googleTotal = google?.counts
        ? Object.values(google.counts).reduce((sum, n) => sum + (Number(n) || 0), 0)
        : 0;
    const googleRadiusM = google?.radiusM;
    const googleDetail = google?.skipped
        ? 'Skipped — set GOOGLE_MAPS_API_KEY in backend .env'
        : google?.counts
            ? `${googleTotal} places within ${googleRadiusM ? (googleRadiusM >= 1000 ? `${(googleRadiusM / 1000).toFixed(1)} km` : `${googleRadiusM} m`) : radiusLabel || 'search area'}`
                + (google.scope === 'nearby' ? ' (nearby search)' : '')
            : google?.error || 'Unavailable';

    const osmDetail = osm?.counts
        ? `${Object.values(osm.counts).reduce((sum, n) => sum + (Number(n) || 0), 0).toLocaleString('en-IN')} POIs`
            + (isPin && radiusLabel ? ` within ${radiusLabel}` : '')
            + (osm.scope === 'pin-bbox' || osm.scope === 'bbox' ? ' (local bbox)' : osm.scope === 'area' ? ' (full district)' : '')
        : osm?.error || 'No data';

    return (
        <div className="ag-card p-4 space-y-4 border-emerald-200/60 bg-emerald-50/40">
            <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold text-emerald-900">Sources for {title || fetched.territoryLabel || 'this pin'}</span>
                <span className="ag-chip bg-white text-xs">{tierLabel}</span>
                {radiusLabel && <span className="ag-chip bg-white text-xs">{radiusLabel} radius</span>}
                {fetched.tookMs != null && (
                    <span className="text-xs text-ink-500">{(fetched.tookMs / 1000).toFixed(1)}s</span>
                )}
            </div>
            {isPin && (
                <p className="text-[11px] text-ink-600 flex items-start gap-1.5">
                    <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    Pin fetch uses your radius for OSM and Google. Wikidata district stats are scaled to the catchment area for urban density only.
                </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                <ProviderChip ok={!!osm?.counts} label="OpenStreetMap" detail={osmDetail} />
                <ProviderChip ok={!!wiki?.stats} label="Wikidata" detail={wikiDetail} />
                <ProviderChip ok={!!google?.counts} label="Google Places" detail={googleDetail} />
            </div>
        </div>
    );
}
